import { type FormEvent, type ReactNode, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { returnCasesHttp } from '@/services/returnCases'
import type { BusinessResourceResponse, BusinessWorkspace } from '@/types/returnCase.types'

export interface BusinessPanelClient {
  businessWorkspace: (caseId: string) => Promise<BusinessWorkspace>
  createBusinessResource: (
    caseId: string,
    path: string,
    input: Record<string, unknown>
  ) => Promise<BusinessResourceResponse>
  evaluateBusiness: (
    caseId: string,
    input: Record<string, unknown>
  ) => Promise<BusinessResourceResponse>
}

interface Props {
  caseId: string
  canEdit: boolean
  sourceType?: 'CUSTOMER_MANUAL' | 'STAFF_MANUAL'
  client?: BusinessPanelClient
  onChanged?: () => void | Promise<void>
}

const value = (form: FormData, key: string) => String(form.get(key) ?? '').trim()
const optional = (form: FormData, key: string) => value(form, key) || undefined

export default function BusinessModulePanel({
  caseId,
  canEdit,
  sourceType = 'STAFF_MANUAL',
  client = returnCasesHttp,
  onChanged,
}: Props) {
  const queryClient = useQueryClient()
  const [error, setError] = useState('')
  const workspace = useQuery({
    queryKey: ['business-module', caseId],
    queryFn: () => client.businessWorkspace(caseId),
  })
  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['business-module', caseId] })
    await onChanged?.()
  }
  const create = useMutation({
    mutationFn: ({ path, input }: { path: string; input: Record<string, unknown> }) =>
      client.createBusinessResource(caseId, path, input),
    onSuccess: async () => {
      setError('')
      await refresh()
    },
    onError: cause =>
      setError(cause instanceof Error ? cause.message : 'The business record was rejected'),
  })
  const evaluate = useMutation({
    mutationFn: () => {
      if (!workspace.data) throw new Error('Business workspace has not loaded')
      return client.evaluateBusiness(caseId, {
        expected_revision: workspace.data.case_revision,
        source_type: sourceType,
        source_reference: 'business-workspace:evaluate',
        reason: 'Recompute governed business, audit, depreciation and election results',
      })
    },
    onSuccess: async () => {
      setError('')
      await refresh()
    },
    onError: cause =>
      setError(cause instanceof Error ? cause.message : 'Business evaluation was rejected'),
  })

  if (workspace.isLoading) return <p>Loading business and profession module…</p>
  if (workspace.isError || !workspace.data) {
    return (
      <div className="alert alert-error" role="alert">
        Business and profession module is unavailable.
      </div>
    )
  }
  const data = workspace.data
  const submit = (path: string, form: HTMLFormElement, fields: Record<string, unknown>) => {
    const formData = new FormData(form)
    const commandFields = new Set(['source_reference', 'reason', 'evidence_binding_id'])
    const cleanFields = Object.fromEntries(
      Object.entries(fields).filter(
        ([key, fieldValue]) => !commandFields.has(key) && fieldValue !== ''
      )
    )
    create.mutate({
      path,
      input: {
        expected_revision: data.case_revision,
        source_type: sourceType,
        source_reference: value(formData, 'source_reference') || `business-ui:${path}`,
        reason: value(formData, 'reason') || `Record ${path} disclosure`,
        evidence_binding_id: optional(formData, 'evidence_binding_id'),
        ...cleanFields,
      },
    })
  }

  return (
    <section
      className="mt-6 rounded border border-border p-4"
      aria-label="Business and profession module"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-semibold">Business, profession and depreciation</h2>
          <p className="text-sm text-text-secondary">
            Individual-return preparation only. Business identity, books and presumptive details do
            not open entity-return or indirect-tax workflows.
          </p>
        </div>
        <span className="badge">
          Rule {data.rule_version} · revision {data.case_revision}
        </span>
      </div>
      {error && (
        <div className="alert alert-error mt-3" role="alert">
          {error}
        </div>
      )}
      {data.warnings.length > 0 && (
        <div className="alert alert-warning mt-3" role="status">
          <strong>Professional review required</strong>
          <ul className="ml-5 list-disc">
            {data.warnings.map(item => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="mt-4 grid gap-3 sm:grid-cols-4" aria-label="Business module totals">
        <Metric label="Businesses/professions" count={data.businesses.length} />
        <Metric label="Book entries" count={data.book_entries.length} />
        <Metric label="Statement lines" count={data.statement_lines.length} />
        <Metric label="Depreciation assets" count={data.depreciation_assets.length} />
      </div>

      <div className="mt-5 overflow-x-auto">
        <table className="w-full text-left text-sm">
          <caption className="mb-2 text-left font-medium">Server-derived results</caption>
          <thead>
            <tr>
              <th>Evaluation</th>
              <th>Status</th>
              <th>Rule</th>
              <th>Explanation</th>
            </tr>
          </thead>
          <tbody>
            {data.current_evaluations.map(item => (
              <tr key={item.id} className="border-t border-border align-top">
                <td className="py-2">{item.evaluation_kind}</td>
                <td>
                  <span className="badge">{item.result_status}</span>
                </td>
                <td>
                  {item.rule_version}
                  <br />
                  <span className="font-mono text-xs">{item.rule_sha256.slice(0, 12)}</span>
                </td>
                <td>
                  <details>
                    <summary>Show thresholds and reasons</summary>
                    <pre className="max-w-xl whitespace-pre-wrap text-xs">
                      {JSON.stringify(item.result_json, null, 2)}
                    </pre>
                  </details>
                </td>
              </tr>
            ))}
            {data.current_evaluations.length === 0 && (
              <tr>
                <td colSpan={4} className="py-3 text-text-secondary">
                  Add source records, then run the governed evaluation.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-5 overflow-x-auto">
        <table className="w-full text-left text-sm">
          <caption className="mb-2 text-left font-medium">
            Form 10-IEA history — record only, no filing action
          </caption>
          <thead>
            <tr>
              <th>Assessment year</th>
              <th>Action</th>
              <th>Filed on</th>
              <th>Portal status</th>
              <th>Version</th>
              <th>Source</th>
            </tr>
          </thead>
          <tbody>
            {data.regime_election_history.map(item => (
              <tr key={item.id} className="border-t border-border align-top">
                <td className="py-2">{item.assessment_year}</td>
                <td>{item.action}</td>
                <td>{item.filed_on}</td>
                <td>{item.portal_status}</td>
                <td>
                  {data.regime_elections.some(current => current.id === item.id)
                    ? 'Current'
                    : 'Superseded'}
                </td>
                <td>{item.source_reference}</td>
              </tr>
            ))}
            {data.regime_election_history.length === 0 && (
              <tr>
                <td colSpan={6} className="py-3 text-text-secondary">
                  No Form 10-IEA history has been recorded.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {canEdit && (
        <div className="mt-6 space-y-3 border-t border-border pt-4">
          <h3 className="font-medium">Add accepted source records</h3>
          <p className="text-sm text-text-secondary">
            Corrections create immutable successor records. Eligibility, audit, depreciation and
            regime conclusions cannot be typed or overridden here.
          </p>
          <Disclosure title="Business or profession master">
            <MasterForm
              disabled={create.isPending}
              onSubmit={(form, fields) => submit('businesses', form, fields)}
            />
          </Disclosure>
          {data.businesses.length > 0 && (
            <>
              <Disclosure title="Receipt, revenue or expense">
                <BookForm
                  businesses={data.businesses}
                  disabled={create.isPending}
                  onSubmit={(form, fields) => submit('book-entries', form, fields)}
                />
              </Disclosure>
              <Disclosure title="Financial statement line">
                <StatementForm
                  businesses={data.businesses}
                  disabled={create.isPending}
                  onSubmit={(form, fields) => submit('statement-lines', form, fields)}
                />
              </Disclosure>
              <Disclosure title="Presumptive declaration">
                <PresumptiveForm
                  businesses={data.businesses}
                  disabled={create.isPending}
                  onSubmit={(form, fields) => submit('presumptive-declarations', form, fields)}
                />
              </Disclosure>
              <Disclosure title="Depreciation block">
                <BlockForm
                  businesses={data.businesses}
                  disabled={create.isPending}
                  onSubmit={(form, fields) => submit('depreciation-blocks', form, fields)}
                />
              </Disclosure>
            </>
          )}
          {data.presumptive_declarations.some(item => item.section === '44AE') && (
            <Disclosure title="Section 44AE goods carriage">
              <VehicleForm
                businesses={data.businesses}
                declarations={data.presumptive_declarations.filter(item => item.section === '44AE')}
                disabled={create.isPending}
                onSubmit={(form, fields) => submit('goods-vehicles', form, fields)}
              />
            </Disclosure>
          )}
          {data.depreciation_blocks.length > 0 && (
            <Disclosure title="Depreciable asset addition or disposal">
              <AssetForm
                blocks={data.depreciation_blocks}
                disabled={create.isPending}
                onSubmit={(form, fields) => submit('depreciation-assets', form, fields)}
              />
            </Disclosure>
          )}
          <Disclosure title="Form 10-IEA history (record only)">
            <ElectionForm
              disabled={create.isPending}
              onSubmit={(form, fields) => submit('regime-elections', form, fields)}
            />
          </Disclosure>
          <button
            type="button"
            className="btn btn-primary"
            disabled={evaluate.isPending || create.isPending}
            onClick={() => evaluate.mutate()}
          >
            Run server-derived business evaluation
          </button>
        </div>
      )}
    </section>
  )
}

type Submit = (form: HTMLFormElement, fields: Record<string, unknown>) => void
type Business = BusinessWorkspace['businesses'][number]

function FormShell({
  children,
  disabled,
  onSubmit,
}: {
  children: ReactNode
  disabled: boolean
  onSubmit: Submit
}) {
  return (
    <form
      className="grid gap-3 sm:grid-cols-2"
      onSubmit={(event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        const form = event.currentTarget
        const entries = Object.fromEntries(new FormData(form).entries())
        onSubmit(form, entries)
      }}
    >
      {children}
      <SourceFields />
      <button className="btn btn-secondary sm:col-span-2" type="submit" disabled={disabled}>
        Save immutable source record
      </button>
    </form>
  )
}

function MasterForm({ disabled, onSubmit }: { disabled: boolean; onSubmit: Submit }) {
  return (
    <FormShell
      disabled={disabled}
      onSubmit={(form, fields) =>
        onSubmit(form, {
          ...fields,
          books_maintained: value(new FormData(form), 'books_maintained') === 'true',
        })
      }
    >
      <Select name="master_kind" label="Kind" options={['BUSINESS', 'PROFESSION']} />
      <Input name="activity_code" label="Nature/activity code" />
      <Select
        name="activity_class"
        label="Activity class"
        options={[
          'GENERAL_BUSINESS',
          'COMMISSION',
          'BROKERAGE',
          'AGENCY',
          'GOODS_CARRIAGE',
          'LEGAL',
          'MEDICAL',
          'ENGINEERING',
          'ARCHITECTURE',
          'ACCOUNTANCY',
          'TECHNICAL_CONSULTANCY',
          'INTERIOR_DECORATION',
          'AUTHORIZED_REPRESENTATIVE',
          'FILM_ARTIST',
          'INFORMATION_TECHNOLOGY',
          'INTRADAY',
          'FNO',
          'SPECULATIVE',
          'SPECIFIED_BUSINESS',
          'OTHER',
        ]}
      />
      <Input name="trade_name" label="Trade/profession name" />
      <Input name="address" label="Place of business" />
      <Input name="commencement_date" label="Commencement date" type="date" />
      <Input name="closure_date" label="Closure date (optional)" type="date" required={false} />
      <Input name="ownership_percent" label="Ownership percent" type="number" defaultValue="100" />
      <Select name="books_maintained" label="Books maintained" options={['true', 'false']} />
      <Select
        name="accounting_method"
        label="Accounting method"
        options={['MERCANTILE', 'CASH', 'MIXED', 'NOT_APPLICABLE']}
      />
      <Select
        name="inventory_method"
        label="Inventory method"
        options={['FIFO', 'WEIGHTED_AVERAGE', 'OTHER', 'NOT_APPLICABLE']}
      />
    </FormShell>
  )
}

function BookForm({
  businesses,
  disabled,
  onSubmit,
}: {
  businesses: Business[]
  disabled: boolean
  onSubmit: Submit
}) {
  return (
    <FormShell
      disabled={disabled}
      onSubmit={(form, fields) =>
        onSubmit(form, {
          ...fields,
          allowable_percent: value(new FormData(form), 'allowable_percent') || '100',
        })
      }
    >
      <BusinessSelect businesses={businesses} />
      <Input name="entry_date" label="Entry date" type="date" />
      <Select
        name="entry_kind"
        label="Entry kind"
        options={[
          'RECEIPT',
          'REVENUE',
          'EXPENSE',
          'INVENTORY',
          'ASSET',
          'LIABILITY',
          'CAPITAL',
          'ADJUSTMENT',
        ]}
      />
      <Select
        name="segment"
        label="Segment"
        options={['REGULAR', 'SPECULATION', 'INTRADAY', 'FNO', 'SPECIFIED_BUSINESS', 'OTHER']}
      />
      <Input name="amount" label="Amount (INR)" type="number" />
      <Select
        name="channel"
        label="Receipt/payment channel"
        options={[
          'CASH',
          'ACCOUNT_PAYEE',
          'PRESCRIBED_ELECTRONIC',
          'NON_ACCOUNT_PAYEE',
          'JOURNAL',
          'NOT_APPLICABLE',
        ]}
      />
      <Select
        name="tax_treatment"
        label="Tax treatment"
        options={[
          'ALLOWABLE',
          'DISALLOWABLE',
          'PARTLY_ALLOWABLE',
          'PERSONAL',
          'TDS_TIMING_REVIEW',
          'STATUTORY_TIMING_REVIEW',
          'REVIEW_REQUIRED',
          'NOT_APPLICABLE',
        ]}
      />
      <Input name="allowable_percent" label="Allowable percent" type="number" defaultValue="100" />
      <Input name="description" label="Description" />
    </FormShell>
  )
}

function StatementForm({
  businesses,
  disabled,
  onSubmit,
}: {
  businesses: Business[]
  disabled: boolean
  onSubmit: Submit
}) {
  return (
    <FormShell disabled={disabled} onSubmit={onSubmit}>
      <BusinessSelect businesses={businesses} />
      <Select
        name="statement_kind"
        label="Statement"
        options={['TRADING', 'PROFIT_AND_LOSS', 'BALANCE_SHEET', 'TRIAL_BALANCE']}
      />
      <Input name="line_code" label="Line code" />
      <Select
        name="side"
        label="Side"
        options={['DEBIT', 'CREDIT', 'ASSET', 'LIABILITY', 'EQUITY', 'MEMO']}
      />
      <Input name="amount" label="Amount (INR)" type="number" />
      <Input name="period_start" label="Period start" type="date" />
      <Input name="period_end" label="Period end" type="date" />
    </FormShell>
  )
}

function PresumptiveForm({
  businesses,
  disabled,
  onSubmit,
}: {
  businesses: Business[]
  disabled: boolean
  onSubmit: Submit
}) {
  return (
    <FormShell
      disabled={disabled}
      onSubmit={(form, fields) =>
        onSubmit(form, {
          ...fields,
          resident: value(new FormData(form), 'resident') === 'true',
          prior_44ad_opt_out: value(new FormData(form), 'prior_44ad_opt_out') === 'true',
        })
      }
    >
      <BusinessSelect businesses={businesses} />
      <Select name="section" label="Section" options={['44AD', '44ADA', '44AE']} />
      <Select name="resident" label="Resident" options={['true', 'false']} />
      <Input
        name="qualifying_digital_receipts"
        label="Qualifying digital receipts"
        type="number"
        defaultValue="0"
      />
      <Input
        name="other_non_cash_receipts"
        label="Other non-cash receipts"
        type="number"
        defaultValue="0"
      />
      <Input
        name="deemed_cash_receipts"
        label="Cash/deemed-cash receipts"
        type="number"
        defaultValue="0"
      />
      <Input name="declared_income" label="Declared income" type="number" />
      <Select name="prior_44ad_opt_out" label="Prior 44AD opt-out" options={['false', 'true']} />
    </FormShell>
  )
}

function VehicleForm({
  businesses,
  declarations,
  disabled,
  onSubmit,
}: {
  businesses: Business[]
  declarations: BusinessWorkspace['presumptive_declarations']
  disabled: boolean
  onSubmit: Submit
}) {
  return (
    <FormShell disabled={disabled} onSubmit={onSubmit}>
      <BusinessSelect businesses={businesses} />
      <Select
        name="declaration_id"
        label="44AE declaration"
        options={declarations.map(item => item.id)}
      />
      <Input name="registration_masked" label="Masked registration" placeholder="******1234" />
      <Input name="gross_vehicle_weight_mt" label="Gross weight (MT)" type="number" />
      <Input name="held_from" label="Held from" type="date" />
      <Input name="held_to" label="Held to" type="date" />
      <Input name="declared_income" label="Declared vehicle income" type="number" />
    </FormShell>
  )
}

function BlockForm({
  businesses,
  disabled,
  onSubmit,
}: {
  businesses: Business[]
  disabled: boolean
  onSubmit: Submit
}) {
  return (
    <FormShell disabled={disabled} onSubmit={onSubmit}>
      <BusinessSelect businesses={businesses} />
      <Select
        name="block_code"
        label="Appendix I block"
        options={[
          'RESIDENTIAL_BUILDING',
          'NON_RESIDENTIAL_BUILDING',
          'TEMPORARY_STRUCTURE',
          'FURNITURE_FITTINGS',
          'GENERAL_PLANT_MACHINERY',
          'COMPUTERS_SOFTWARE',
          'INTANGIBLE_ASSETS',
        ]}
      />
      <Input name="opening_wdv" label="Opening WDV" type="number" />
    </FormShell>
  )
}

function AssetForm({
  blocks,
  disabled,
  onSubmit,
}: {
  blocks: BusinessWorkspace['depreciation_blocks']
  disabled: boolean
  onSubmit: Submit
}) {
  return (
    <FormShell disabled={disabled} onSubmit={onSubmit}>
      <Select name="block_id" label="Depreciation block" options={blocks.map(item => item.id)} />
      <Input name="description" label="Asset description" />
      <Input name="acquisition_date" label="Acquisition date" type="date" />
      <Input name="put_to_use_date" label="Put-to-use date" type="date" />
      <Input name="actual_cost" label="Actual cost" type="number" />
      <Input name="disposal_date" label="Disposal date (optional)" type="date" required={false} />
      <Input name="disposal_value" label="Disposal value" type="number" defaultValue="0" />
    </FormShell>
  )
}

function ElectionForm({ disabled, onSubmit }: { disabled: boolean; onSubmit: Submit }) {
  return (
    <FormShell disabled={disabled} onSubmit={onSubmit}>
      <Input name="assessment_year" label="Assessment year" placeholder="2026-27" />
      <Select name="action" label="Election action" options={['OPT_OUT_NEW', 'RE_ENTER_NEW']} />
      <Input name="filed_on" label="Filed on" type="date" />
      <Input name="due_date" label="Applicable due date" type="date" />
      <Input
        name="acknowledgement_masked"
        label="Masked acknowledgement"
        placeholder="********1234"
      />
      <Select name="portal_status" label="Portal status" options={['VALID', 'INVALID']} />
    </FormShell>
  )
}

function SourceFields() {
  return (
    <>
      <Input name="evidence_binding_id" label="Evidence binding ID (optional)" required={false} />
      <Input name="source_reference" label="Source reference (optional)" required={false} />
      <Input name="reason" label="Reason (optional)" required={false} />
    </>
  )
}

function BusinessSelect({ businesses }: { businesses: Business[] }) {
  return (
    <label className="text-sm">
      Business/profession
      <select className="input mt-1 w-full" name="business_id" required>
        {businesses.map(item => (
          <option key={item.id} value={item.id}>
            {item.trade_name} · {item.master_kind}
          </option>
        ))}
      </select>
    </label>
  )
}

function Disclosure({ title, children }: { title: string; children: ReactNode }) {
  return (
    <details className="rounded border border-border p-3">
      <summary className="cursor-pointer font-medium">{title}</summary>
      <div className="mt-3">{children}</div>
    </details>
  )
}

function Metric({ label, count }: { label: string; count: number }) {
  return (
    <div className="rounded border border-border p-3">
      <div className="text-xs text-text-secondary">{label}</div>
      <div className="text-lg font-semibold">{count}</div>
    </div>
  )
}

function Input({
  name,
  label,
  type = 'text',
  required = true,
  defaultValue,
  placeholder,
}: {
  name: string
  label: string
  type?: string
  required?: boolean
  defaultValue?: string
  placeholder?: string
}) {
  return (
    <label className="text-sm">
      {label}
      <input
        className="input mt-1 w-full"
        name={name}
        type={type}
        required={required}
        defaultValue={defaultValue}
        placeholder={placeholder}
        min={type === 'number' ? 0 : undefined}
        step={type === 'number' ? 'any' : undefined}
      />
    </label>
  )
}

function Select({ name, label, options }: { name: string; label: string; options: string[] }) {
  return (
    <label className="text-sm">
      {label}
      <select className="input mt-1 w-full" name={name} required>
        {options.map(item => (
          <option key={item} value={item}>
            {item.replace(/_/g, ' ')}
          </option>
        ))}
      </select>
    </label>
  )
}
